import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

import {
  AlphaFieldRenderer,
  collectErrors,
  isWideField,
  toSubmitValues,
  withDefaults,
} from './AlphaFieldRenderer';
import { readCardDraft, writeCardDraft } from './cardDraft';
import {
  flattenFields,
  getPath,
  isRecord,
  setPath,
  visibilityOf,
} from './cardModel';
import {
  BlockedNote,
  CardFooter,
  CardFrame,
  CardHeader,
  CardLead,
  CardRoot,
  CardTitle,
  FieldCell,
  FieldGrid,
  FooterSpacer,
  PrimaryButton,
  SafetyNote,
  SecondaryButton,
  TitlePrefix,
} from './cardStyles';
import {
  AlphaCard,
  CardHandlers,
  CardServerError,
  FieldErrors,
  FormField,
} from './cardTypes';
import {
  ANOTHER_REQUEST_TEXT,
  CardFailureAlert,
  SUBMIT_FAILED_TEXT,
  addOnce,
  belongsTo,
  useCardActions,
  useCardFocus,
} from './useCardActions';

export interface AlphaAskCardProps extends CardHandlers {
  token: string;
  card: AlphaCard;
  serverError?: CardServerError;
  submitting?: boolean;
  cancelling?: boolean;
  blocked?: boolean;
  blockedMessage?: string;
  cancelBlocked?: boolean;
}

export const ASK_SAFETY_TEXT = 'Alpha never asks for passwords or codes.';

const SUBMIT_FAILED = SUBMIT_FAILED_TEXT.ask;
const SKIP_FAILED = "Couldn't skip the question. Try again.";

export const AlphaAskCard = ({
  token,
  card,
  serverError,
  submitting,
  cancelling: cancellingElsewhere,
  blocked = false,
  blockedMessage = ANOTHER_REQUEST_TEXT,
  cancelBlocked = false,
  loadOptions,
  onSubmit,
  onCancel,
}: AlphaAskCardProps): JSX.Element => {
  const fields = useMemo(() => {
    if (card.form != null) {
      return flattenFields(card.form);
    }
    return card.editableFields ?? [];
  }, [card.form, card.editableFields]);
  const formTitle = card.form?.title ?? '';
  const title =
    card.title != null && card.title !== '' ? card.title : formTitle;
  const intro = card.form?.steps[0]?.description ?? '';
  const [values, setValues] = useState<Record<string, unknown>>(
    () =>
      readCardDraft(token)?.values ??
      withDefaults(fields, isRecord(card.values) ? card.values : {}),
  );
  const [attempted, setAttempted] = useState(false);
  const [touched, setTouched] = useState<string[]>([]);
  const interacted = useRef(false);
  const rootRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = `alpha-ask-${useId().replace(/:/g, '')}`;
  const blockedId = `${titleId}-blocked`;
  const { requestFocus, holdFocus } = useCardFocus(rootRef, titleRef);
  const actions = useCardActions({
    token,
    fields,
    serverError,
    values,
    submitting,
    cancellingElsewhere,
    onSubmit,
    onCancel,
    submitFailedMessage: SUBMIT_FAILED,
    cancelFailedMessage: SKIP_FAILED,
    holdFocus,
    onServerFailure: (fieldErrors, initialFailure) => {
      if (!initialFailure && Object.keys(fieldErrors).length > 0) {
        requestFocus('invalid');
      }
    },
  });
  const { busy, cancelling, failure, serverFieldErrors } = actions;

  useEffect(() => {
    if (interacted.current) {
      writeCardDraft(token, { values });
    }
  }, [token, values]);

  const visibility = visibilityOf(fields, values);
  const localErrors = collectErrors(fields, values, '', visibility);
  const visibleLocal: FieldErrors = attempted
    ? localErrors
    : Object.fromEntries(
        Object.entries(localErrors).filter(([key]) =>
          touched.some(touchedKey => belongsTo(key, touchedKey)),
        ),
      );
  const shownErrors = { ...serverFieldErrors, ...visibleLocal };

  const changeField =
    (field: FormField) =>
    (next: unknown): void => {
      interacted.current = true;
      setValues(previous => setPath(previous, field.key, next));
      actions.markEdited(field.key);
    };

  const touchField = (field: FormField) => (): void => {
    setTouched(previous => addOnce(previous, field.key));
  };

  const skip = (): void => {
    if (!cancelBlocked) {
      actions.cancel();
    }
  };

  const send = (): void => {
    if (blocked) {
      return;
    }
    if (Object.keys(localErrors).length > 0) {
      setAttempted(true);
      requestFocus('invalid');
      return;
    }
    actions.submit(toSubmitValues(fields, values));
  };

  return (
    <CardFrame>
      <CardRoot ref={rootRef} aria-labelledby={titleId} aria-busy={busy}>
        <CardHeader>
          <CardTitle
            ref={titleRef}
            id={titleId}
            tabIndex={-1}
            data-alpha-card-title=""
          >
            <TitlePrefix>Alpha is asking:</TitlePrefix> {title}
          </CardTitle>
          {intro !== '' && <CardLead>{intro}</CardLead>}
          <SafetyNote>{ASK_SAFETY_TEXT}</SafetyNote>
        </CardHeader>
        <CardFailureAlert
          failure={failure}
          busy={busy}
          canRetrySubmit={!blocked}
          onRetrySubmit={send}
          onRetryCancel={skip}
        />
        <FieldGrid>
          {fields
            .filter(field => visibility.field(field))
            .map(field => (
              <FieldCell key={field.key} wide={isWideField(field)}>
                <AlphaFieldRenderer
                  field={field}
                  value={getPath(values, field.key)}
                  scope={visibility}
                  errors={shownErrors}
                  disabled={busy}
                  loadOptions={loadOptions}
                  prefill={isRecord(card.values) ? card.values : undefined}
                  onChange={changeField(field)}
                  onBlur={touchField(field)}
                />
              </FieldCell>
            ))}
        </FieldGrid>
        {(blocked || cancelBlocked) && (
          <BlockedNote id={blockedId}>{blockedMessage}</BlockedNote>
        )}
        <CardFooter>
          <SecondaryButton
            type="button"
            disabled={busy || cancelling}
            aria-disabled={cancelBlocked || undefined}
            aria-describedby={cancelBlocked ? blockedId : undefined}
            onClick={skip}
          >
            Skip
          </SecondaryButton>
          <FooterSpacer />
          <PrimaryButton
            type="button"
            disabled={busy || cancelling}
            aria-disabled={blocked || undefined}
            aria-describedby={blocked ? blockedId : undefined}
            onClick={send}
          >
            {busy ? 'Send answers…' : 'Send answers'}
          </PrimaryButton>
        </CardFooter>
      </CardRoot>
    </CardFrame>
  );
};
